import { ListItem, makeStyles, Text } from '@rneui/themed'
import { QueryErrors } from 'app/concept_query_error_context'
import { t } from 'app/i18l'
import { isEmpty } from 'lodash'
import React, { useState } from 'react'
import { View } from 'react-native'
import Loading from './loading'
import { RoundedButton } from './rounded-button'

interface Props {
  isLoading: boolean
  loadingMessage?: string
  error: any
  children?: React.ReactNode
  errors?: QueryErrors
  onRetry?: () => Promise<void>
}

export function StatusBoundary({
  isLoading,
  loadingMessage,
  error,
  children,
  errors,
  onRetry,
}: Props): React.JSX.Element {
  const styles = useStyles()

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.text}>{error.message}</Text>
      </View>
    )
  }

  if (errors) {
    if (!isEmpty(Object.values(errors))) {
      return <ErrorsListView errors={errors} onRetry={onRetry} />
    }
  }

  if (isLoading) {
    return <Loading text={loadingMessage} />
  }

  return <>{children}</>
}

interface ErrorsListProps {
  errors: QueryErrors
  onRetry?: () => Promise<void>
}

const ErrorsListView = ({ errors, onRetry }: ErrorsListProps) => {
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)

  const onButtonPress = async () => {
    setLoading(true)
    if (onRetry) {
      await onRetry()
    }
    setLoading(false)
  }

  const styles = useStyles()
  return (
    <View style={styles.errorsViewContainer}>
      <RoundedButton
        title={`${t('retry', { locale: 'en' })} / ${t('retry', {
          locale: 'es',
        })}`}
        onPress={onButtonPress}
        loading={isLoading}
        emphasized
      />
      <Text
        style={styles.showErrorsTextStyle}
        onPress={() => setShowErrors(!showErrors)}
      >
        {`${t(showErrors ? 'hideErrors' : 'showErrors', {
          locale: 'en',
        })} / ${t(showErrors ? 'hideErrors' : 'showErrors', { locale: 'es' })}`}
      </Text>
      {showErrors
        ? Object.keys(errors).map((errorKey, index) => {
            return errors[errorKey] ? (
              <ListItem
                key={index}
                bottomDivider
                containerStyle={styles.errorItemContainer}
              >
                <ListItem.Content>
                  <ListItem.Title>{errorKey}</ListItem.Title>
                  <ListItem.Subtitle>
                    {errors[errorKey].message}
                  </ListItem.Subtitle>
                </ListItem.Content>
              </ListItem>
            ) : null
          })
        : null}
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors?.white,
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: 40,
  },
  text: {
    fontSize: 24,
    textAlign: 'center',
  },
  errorsViewContainer: {
    flex: 1,
    backgroundColor: theme.colors?.white,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorsListContainer: {
    alignItems: 'stretch',
  },
  errorItemContainer: {
    alignSelf: 'center',
  },
  showErrorsTextStyle: {
    marginTop: 10,
    textAlign: 'center',
    color: theme.colors?.grey2,
    textDecorationLine: 'underline',
  },
}))
